/**
 * Status -> Tailwind class maps for the Orchestration Status graph (PWA-2150).
 *
 * Class strings are full literals (no runtime concatenation) so Tailwind's
 * content scan keeps them. No hardcoded hex anywhere -- SVG edges/arrows use
 * stroke and fill utilities (CLAUDE.md section 16). Dark-first; light fallbacks
 * included so light mode is not broken.
 */

import { SlotStatus, StepStatus } from "../../../../types/orchestration";

/** Outer node card classes by step status. */
export const NODE_CLASS: Record<StepStatus, string> = {
  succeeded:
    "bg-white dark:bg-rp-surface-raised border-green-500/60 dark:border-green-500/50",
  running:
    "bg-amber-50 dark:bg-rp-amber/10 border-rp-amber shadow-[0_0_0_3px_rgba(204,122,0,0.18)]",
  failed:
    "bg-red-50 dark:bg-red-500/10 border-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.16)]",
  pending:
    "bg-gray-50 dark:bg-rp-surface-sunken border-dashed border-gray-300 dark:border-gray-600 opacity-70",
  skipped:
    "bg-gray-100 dark:bg-[#161616] border-gray-300 dark:border-gray-700 opacity-85",
};

/** SVG edge stroke utility by the SOURCE step status. */
export const EDGE_STROKE: Record<StepStatus, string> = {
  succeeded: "stroke-green-500",
  running: "stroke-rp-amber",
  failed: "stroke-red-500",
  pending: "stroke-gray-300 dark:stroke-gray-600",
  skipped: "stroke-gray-300 dark:stroke-gray-600",
};

/** SVG arrowhead fill utility by the SOURCE step status. */
export const ARROW_FILL: Record<StepStatus, string> = {
  succeeded: "fill-green-500",
  running: "fill-rp-amber",
  failed: "fill-red-500",
  pending: "fill-gray-300 dark:fill-gray-600",
  skipped: "fill-gray-300 dark:fill-gray-600",
};

/** Slot status pill classes. */
export const SLOT_PILL_CLASS: Record<SlotStatus, string> = {
  in_progress:
    "bg-amber-100 dark:bg-rp-amber/15 text-rp-amber border border-rp-amber/40",
  completed:
    "bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/40",
  completed_with_failures:
    "bg-amber-100 dark:bg-rp-amber/15 text-amber-700 dark:text-rp-amber border border-rp-amber/40",
  failed:
    "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/40",
};

/** Big elapsed/duration value color by slot status. */
export const SLOT_BIG_CLASS: Record<SlotStatus, string> = {
  in_progress: "text-rp-amber",
  completed: "text-green-600 dark:text-green-500",
  completed_with_failures: "text-amber-600 dark:text-rp-amber",
  failed: "text-red-600 dark:text-red-500",
};

export const SLOT_STATUS_LABEL: Record<SlotStatus, string> = {
  in_progress: "In Progress",
  completed: "Completed",
  completed_with_failures: "Completed w/ failures",
  failed: "Failed",
};

/** Popover status tag classes. */
export const STATUS_TAG_CLASS: Record<StepStatus, string> = {
  succeeded: "bg-green-500/15 text-green-600 dark:text-green-400",
  running: "bg-rp-amber/15 text-rp-amber",
  failed: "bg-red-500/15 text-red-600 dark:text-red-400",
  pending: "bg-gray-200 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400",
  skipped: "bg-gray-200 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400",
};
